<template>
    <div>
        <v-container grid-list-xs>
            <v-sheet class="pa-5" elevation="3">
                <Form>
                    <PageHeader>
                        <template #header>
                            <h4>{{ $t(`terms.${messagesApplyMode}-titleHeading`) }}</h4>
                        </template>
                    </PageHeader>

                    <div id="hfd-iframe-container" class="tc-container">
                        <PDFViewer v-if="pdfUrl" :pdf-url="pdfUrl" @pdf-loaded="handleTermsLoaded" />
                    </div>

                    <v-row justify="center">
                        <v-col class="pa-0 mx-2 my-0">
                            <CheckboxField
                                id="checkbox1"
                                v-model="printedCopyCheckbox"
                                class="check-box"
                                color="secondary"
                            >
                                {{
                                    $t("terms.downpayment", {
                                        downpaymentAmount: downPaymentDollars,
                                        providerKey: providerKey,
                                    })
                                }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row class="mt-0" justify="center">
                        <v-col class="pa-0 mx-2 my-0">
                            <CheckboxField
                                id="checkbox2"
                                v-model="termsCheckbox"
                                class="check-box"
                                :disabled="!printedCopyCheckbox"
                            >
                                {{ $t("terms.terms-and-conditions", { providerKey: providerKey }) }}
                            </CheckboxField>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col>
                            <SignaturePad
                                v-model="application.signatureData"
                                class="mt-5"
                                :disabled="!printedCopyCheckbox"
                                @signature-valid="isSignatureValid"
                            />
                        </v-col>
                    </v-row>

                    <v-card-actions class="pb-12 px-0">
                        <v-row justify="space-between">
                            <v-col>
                                <BackButton class="mr-4" color="primary" rounded>
                                    {{ $t("action.back") }}
                                </BackButton>
                            </v-col>
                            <v-col class="text-right">
                                <SubmitButton
                                    class="secondary submit-button"
                                    :disabled="
                                        !termsCheckbox ||
                                        !printedCopyCheckbox ||
                                        !validSignature ||
                                        isStipulationPending ||
                                        !bottomReached
                                    "
                                    :submitting="submitting"
                                    rounded
                                    @click="accept"
                                >
                                    {{ $t("terms.submit", { providerKey: providerKey }) }}
                                </SubmitButton>
                            </v-col>
                        </v-row>
                    </v-card-actions>
                </Form>
                <ResponsiveImage :image-url="footerLogo" width="100px" class="footer-logo-center" />
            </v-sheet>
            <AcceptOfferErrorModal
                v-model="showAcceptOfferErrorModal"
                :error-message="errorMessage"
                @go-to-payment-methods="goToPaymentMethods"
                @close-modal="closeAcceptOfferErrorModal"
            >
            </AcceptOfferErrorModal>
        </v-container>
    </div>
</template>

<script>
    import BackButton from "@/components/button/BackButton.vue"
    import Providers from "@/model/provider/Providers"
    import { applicationState, sessionState } from "@/state"
    import StipulationViewMixin from "@/components/stipulation/StipulationViewMixin.vue"
    import { getMessagesApplyMode } from "@/model/apply/ApplyMode"
    import { mapState } from "vuex"
    import AcceptOfferErrorModal from "@/components/modal/AcceptOfferErrorModal.vue"
    import { goto } from "@/util/navigation"
    import ProviderState from "@/state/ProviderState"
    import ProviderKey from "@/model/provider/ProviderKey"
    import Currency from "@/util/currency"
    import { applyService, googleAnalyticsService, logService } from "@/service"
    import GoogleAnalyticEvents from "@/model/google/GoogleAnalyticEvents"
    import { removeAcceptOfferError, retrieveAcceptOfferError } from "@/util/acceptOfferError"
    import PDFViewer from "@/components/terms/PDFViewer.vue"

    export default {
        components: { PDFViewer, AcceptOfferErrorModal, BackButton },
        mixins: [StipulationViewMixin],
        data() {
            return {
                pdfUrl: "",
                providerInfo: Providers[sessionState.dynamicProviderKey.providerKey],
                providerKey: sessionState.providerKey,
                applyMode: applicationState.applyMode,
                downPaymentDollars: Currency.centsToDollars(applicationState.selectedPaymentPlan.downPaymentCents),
                termsCheckbox: false,
                printedCopyCheckbox: false,
                validSignature: false,
                submitting: false,
                showAcceptOfferErrorModal: false,
                errorMessage: "",
                bottomReached: false,
            }
        },

        computed: {
            ...mapState("application", {
                application: state => state.data,
            }),

            messagesApplyMode() {
                return getMessagesApplyMode(this.applyMode)
            },
            footerLogo() {
                const response = ProviderState.getPreAuthResponse()
                return Providers[ProviderKey.HFD_STANDARD]?.footerLogo[response.providerProductName]
            },
        },

        async created() {
            this.acceptOfferError = retrieveAcceptOfferError()

            if (this.acceptOfferError) {
                this.showAcceptOfferErrorModal = true
                this.errorMessage = this.acceptOfferError.provider?.errors[0]?.errorMessage
            }

            const documentResponse = await applyService.getDocuments(sessionState.providerKey)
            const url = documentResponse.documents[documentResponse.documents.length - 1]?.uri || ""
            if (!url) {
                logService.info("Unable to retrieve terms document")
                await this.$router.push(`/providers/${sessionState.providerKey}/error`)
            }
            this.pdfUrl = url
        },

        methods: {
            handleTermsLoaded() {
                const container = document.querySelector("#hfd-iframe-container")
                this.handleTermsScroll(
                    container,
                    () => {
                        if (!this.bottomReached) {
                            console.log("HFD bottom of terms reached!")
                            logService.info("HFD bottom of terms reached!")
                        }
                        this.bottomReached = true
                    },
                    100
                )
            },

            handleTermsScroll(el, onBottom, threshold) {
                const handler = () => {
                    const { scrollTop, clientHeight, scrollHeight } = el
                    const atBottom = scrollTop + clientHeight >= scrollHeight - threshold

                    if (atBottom) onBottom()
                }
                el.addEventListener("scroll", handler)
            },

            async accept() {
                this.submitting = true
                try {
                    const providerKey = sessionState.providerKey
                    googleAnalyticsService.buttonEvent(
                        GoogleAnalyticEvents.ACCEPT_OFFER_SUBMITTED,
                        "HFD Accept Offer Submitted"
                    )
                    await this.$router.push(`/providers/${providerKey}/acceptOfferSubmit`)
                } finally {
                    this.submitting = false
                }
            },

            isSignatureValid(validSignature) {
                this.validSignature = validSignature
            },

            closeAcceptOfferErrorModal() {
                removeAcceptOfferError()
                this.showAcceptOfferErrorModal = false
            },

            goToPaymentMethods() {
                goto(`/providers/${sessionState.providerKey}/downpayment`)
            },
        },
    }
</script>

<style lang="scss" scoped>
    .v-btn {
        font-size: 16px !important;
        padding-top: 10px !important;
        padding-bottom: 10px !important;
    }

    .tc-container {
        height: 60vh;
        overflow-y: scroll;
        margin-bottom: 24px;
    }

    .term-verbiage {
        line-height: 24px;
        font-size: 16px;
    }

    .check-box {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .submit-button {
        height: fit-content !important;
        min-width: 160px;
        white-space: normal;
        display: block;

        @media #{map-get($display-breakpoints, "sm-and-up")} {
            display: inline-flex;
            white-space: nowrap;
            width: auto;
            height: auto;
        }
    }
</style>
